import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

interface KpiDefinitionRow {
  readonly kpiDefinitionId: string;
  readonly kpiCode: string;
  readonly kpiName: string;
  readonly unit: string;
  readonly targetValue: number;
  readonly direction: string;
  readonly status: string;
}

interface LatestKpiValue {
  readonly kpiCode: string;
  readonly kpiName: string;
  readonly value: number;
  readonly unit: string;
  readonly status: string;
}

interface NewKpiForm {
  kpiCode: string;
  kpiName: string;
  formulaExpression: string;
  sourceQuery: string;
  unit: string;
}

const EMPTY_KPI: NewKpiForm = { kpiCode: '', kpiName: '', formulaExpression: '', sourceQuery: '', unit: 'PERCENTAGE' };
const KPI_UNITS = ['PERCENTAGE', 'DAYS', 'HOURS', 'COUNT', 'CURRENCY'] as const;

/** L16.05 — KPI admin (`/kpis`). Lists tenant KPI definitions and provides
 *  create (M4), calculate (kpis/calculate) and latest-value (kpis/latest)
 *  authoring so the "Admin" screen is genuinely actionable. */
export function M16KpiAdminPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<KpiDefinitionRow[] | null>(null);
  const [latest, setLatest] = useState<LatestKpiValue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<NewKpiForm>(EMPTY_KPI);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [period, setPeriod] = useState<{ start: string; end: string }>({ start: '', end: '' });

  async function loadDefs(): Promise<void> {
    setRows(await apiRequest<KpiDefinitionRow[]>('/v1/m16/dashboards/kpis/definitions', { token }));
  }

  async function loadLatest(): Promise<void> {
    try {
      setLatest(await apiRequest<LatestKpiValue[]>('/v1/m16/dashboards/kpis/latest', { token }));
    } catch (e) {
      // An unread table must not pass for "nothing calculated yet".
      setActionError((e as Error).message);
    }
  }

  useEffect(() => {
    let alive = true;
    apiRequest<KpiDefinitionRow[]>('/v1/m16/dashboards/kpis/definitions', { token })
      .then((r) => { if (alive) setRows(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    apiRequest<LatestKpiValue[]>('/v1/m16/dashboards/kpis/latest', { token })
      .then((r) => { if (alive) setLatest(r); })
      .catch((e) => { if (alive) setActionError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  async function createKpi(): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      await apiRequest('/v1/m16/dashboards/kpis/definitions', {
        method: 'POST',
        token,
        body: {
          kpiCode: form.kpiCode,
          kpiName: form.kpiName,
          formulaExpression: form.formulaExpression,
          sourceQuery: form.sourceQuery,
          unit: form.unit,
        },
      });
      setForm(EMPTY_KPI);
      await loadDefs();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function calculateKpi(id: string): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      await apiRequest('/v1/m16/dashboards/kpis/calculate', {
        method: 'POST',
        token,
        body: { kpiDefinitionId: id, periodStart: period.start, periodEnd: period.end },
      });
      await loadLatest();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m16.kpiAdmin.loading')}</p>;

  // CreateKpiDefinitionDto: kpiCode and kpiName min 3, formulaExpression and
  // sourceQuery non-empty. unit comes from a select so it always has a value.
  const canCreateKpi =
    form.kpiCode.trim().length >= 3 &&
    form.kpiName.trim().length >= 3 &&
    form.formulaExpression.trim().length > 0 &&
    form.sourceQuery.trim().length > 0;

  // CalculateKpiDto requires both period bounds.
  const canCalculate = period.start.trim().length > 0 && period.end.trim().length > 0;

  return (
    <main aria-label="kpi-admin">
      <h1>{t('m16.kpiAdmin.title')}</h1>

      <section aria-label="kpi-builder">
        <h2>{t('m16.kpiAdmin.newKpi')}</h2>
        <input aria-label="new-kpi-code" data-testid="new-kpi-code" placeholder={t('m16.kpiAdmin.placeholderCode')}
          value={form.kpiCode} onChange={(e) => setForm((f) => ({ ...f, kpiCode: e.target.value }))} />
        <input aria-label="new-kpi-name" data-testid="new-kpi-name" placeholder={t('m16.kpiAdmin.placeholderName')}
          value={form.kpiName} onChange={(e) => setForm((f) => ({ ...f, kpiName: e.target.value }))} />
        <input aria-label="new-kpi-formula" data-testid="new-kpi-formula" placeholder={t('m16.kpiAdmin.placeholderFormula')}
          value={form.formulaExpression} onChange={(e) => setForm((f) => ({ ...f, formulaExpression: e.target.value }))} />
        <input aria-label="new-kpi-query" data-testid="new-kpi-query" placeholder={t('m16.kpiAdmin.placeholderQuery')}
          value={form.sourceQuery} onChange={(e) => setForm((f) => ({ ...f, sourceQuery: e.target.value }))} />
        <select aria-label="new-kpi-unit" data-testid="new-kpi-unit"
          value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}>
          {KPI_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <button type="button" data-testid="create-kpi-btn" disabled={busy || !canCreateKpi} onClick={createKpi}>{t('m16.kpiAdmin.createBtn')}</button>
      </section>

      <section aria-label="kpi-calc-period">
        <DateInput aria-label="calc-period-start" data-testid="calc-period-start" placeholder={t('m16.kpiAdmin.placeholderPeriodStart')}
          value={period.start} onValueChange={(nextValue) => setPeriod((p) => ({ ...p, start: nextValue }))} />
        <DateInput aria-label="calc-period-end" data-testid="calc-period-end" placeholder={t('m16.kpiAdmin.placeholderPeriodEnd')}
          value={period.end} onValueChange={(nextValue) => setPeriod((p) => ({ ...p, end: nextValue }))} />
      </section>
      {actionError && <p role="alert">{actionError}</p>}

      <table aria-label="kpi-definitions-table">
        <thead>
          <tr><th>{t('m16.kpiAdmin.colCode')}</th><th>{t('m16.kpiAdmin.colName')}</th><th>{t('m16.kpiAdmin.colUnit')}</th><th>{t('m16.kpiAdmin.colTarget')}</th><th>{t('m16.kpiAdmin.colDirection')}</th><th>{t('m16.kpiAdmin.colStatus')}</th><th>{t('m16.kpiAdmin.colCalculate')}</th></tr>
        </thead>
        <tbody>
          {rows.map((k) => (
            <tr key={k.kpiDefinitionId}>
              <td>{k.kpiCode}</td>
              <td>{k.kpiName}</td>
              <td>{k.unit}</td>
              <td>{k.targetValue}</td>
              <td>{k.direction}</td>
              <td>{k.status}</td>
              <td>
                <button type="button" aria-label={`calculate-${k.kpiCode}`} disabled={busy || !canCalculate}
                  onClick={() => calculateKpi(k.kpiDefinitionId)}>{t('m16.kpiAdmin.calculateBtn')}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <section aria-label="kpi-latest">
        <h2>{t('m16.kpiAdmin.latestValues')}</h2>
        <table aria-label="kpi-latest-table">
          <thead><tr><th>{t('m16.kpiAdmin.latestColCode')}</th><th>{t('m16.kpiAdmin.latestColValue')}</th><th>{t('m16.kpiAdmin.latestColUnit')}</th><th>{t('m16.kpiAdmin.latestColStatus')}</th></tr></thead>
          <tbody>
            {latest.map((l) => (
              <tr key={l.kpiCode} data-testid={`latest-${l.kpiCode}`}>
                <td>{l.kpiCode}</td>
                <td>{l.value}</td>
                <td>{l.unit}</td>
                <td>{l.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </main>
  );
}
